import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import type { ZapIFrameConfig } from '@ctw/shared/types/zap-message';
import { getApiServiceBaseUrl, getEnvFromCurrentHostname } from '@ctw/shared/utils/environments';
import { tw } from '@ctw/shared/utils/tailwind';
import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSalesforceEnvironment } from './salesforce-environment-context';
import { useSalesforceUser } from './salesforce-user-context';

interface ZusTokenContextProps {
  zusAccessToken?: string;
}

const defaultContextValue: ZusTokenContextProps = {};

export const ZusTokenContext = createContext<ZusTokenContextProps>(defaultContextValue);

interface ZusTokenProviderProps extends PropsWithChildren {
  zapConfig: Partial<ZapIFrameConfig> | null;
}

export const ZusTokenProvider = ({ children, zapConfig }: ZusTokenProviderProps) => {
  const [zusAccessToken, setZusAccessToken] = useState<string>();
  const salesforceUser = useSalesforceUser();
  const salesforceEnvironment = useSalesforceEnvironment();
  const env = getEnvFromCurrentHostname({ fallbackEnv: zapConfig?.devProps?.fallbackEnv });

  const ehrHooksUrl = getApiServiceBaseUrl({ service: 'ehr-hooks', env });

  useEffect(() => {
    const fetchZusToken = async () => {
      try {
        const tokenResponse = await fetch(`${ehrHooksUrl}/token/salesforce`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: {
              type: 'token-request',
              attributes: {
                accessToken: salesforceUser.user?.access_token,
                organizationId: salesforceEnvironment.environment?.organizationId,
              },
            },
          }),
        });

        if (tokenResponse.status !== 200) {
          throw new Error('Could not get Zus token');
        }

        setZusAccessToken((await tokenResponse.json()).access_token);
      } catch (_error) {
        throw new Error('An error occurred while fetching the Zus token.');
      }
    };

    void fetchZusToken();
  }, [salesforceUser, salesforceEnvironment, ehrHooksUrl]);

  const contextValue = useMemo(() => ({ zusAccessToken }), [zusAccessToken]);

  if (!zusAccessToken) {
    return (
      <LoadingSpinner
        className={tw`fixed inset-0 flex h-screen items-center justify-center`}
        message="Logging in to Zus"
      />
    );
  }

  return <ZusTokenContext.Provider value={contextValue}>{children}</ZusTokenContext.Provider>;
};

export const useZusToken = () => useContext(ZusTokenContext);
